import {inject, Injectable} from "@angular/core";
import {PermissionScope} from "./permission-scope.enum";
import {PermissionGrant} from "./permission-grant.enum";
import {StateService} from "../state/state.service";
import {BehaviorSubject, Observable} from "rxjs";
import {Permissions} from "./permissions.item";
import {filter} from "rxjs/operators";
import {isDefined} from "../../libs/utils/epic.utils";
import {OrgDto} from "../../repositories/dto/org.dto";
import {Feature} from "../../libs/enum/feature.enum";

@Injectable({providedIn: 'root'})
export class PermissionService {

  public static readonly NOT_FEATURE_SCOPES: PermissionScope[] = [
    PermissionScope.CONTACT,
    PermissionScope.CONTACT_EMPLOYEE,
    // PermissionScope.VEHICLE, // Currently not implemented
    PermissionScope.ARTICLE,
    PermissionScope.CATALOG,
    PermissionScope.INVOICE,
    PermissionScope.INVOICE_CANCEL,
    PermissionScope.INVOICE_PAYMENT,
    PermissionScope.RECEIPT,
    PermissionScope.SETTINGS,
    PermissionScope.SETTINGS_ORG,
    PermissionScope.SETTINGS_USER,
    PermissionScope.SETTINGS_INVOICE,
    PermissionScope.SETTINGS_VAT_RATE,
    PermissionScope.FEATURE,
    PermissionScope.BILLING,
    PermissionScope.STATISTICS,
    PermissionScope.ORG_WALLET,
    PermissionScope.CORRESPONDENCE,
    PermissionScope.EXPORT_CSV,
    PermissionScope.IRON_BACKUP,
    PermissionScope.SUPPORT,
    PermissionScope.AGREEMENT,
  ];

  private readonly stateService: StateService = inject(StateService);

  private readonly changed: BehaviorSubject<Permissions | undefined> = new BehaviorSubject<Permissions | undefined>(undefined);
  private permissions: Map<PermissionScope, PermissionGrant> = new Map<PermissionScope, PermissionGrant>();
  private perms!: Permissions;
  private org: OrgDto | undefined;

  constructor() {
    this.stateService.getOrg().subscribe((org) => {
      this.org = org;
      this.permissions = isDefined(org) ? org.permissions : new Map();
      this.perms = new Permissions(this.permissions);
      this.changed.next(this.perms);
    });
  }

  public getPerms(): Permissions {
    return this.perms;
  }

  public accChanged(): Observable<Permissions> {
    return this.changed.asObservable().pipe(filter((p) => isDefined(p)));
  }

  public isAllow(scope: PermissionScope, grant: PermissionGrant): boolean {
    if (scope === PermissionScope.ANY && Array.from(this.permissions.values()).some((p) => this.isGranted(p, grant))) {
      return true;
    }
    return this.isGranted(this.getPerms().getByScope(scope).grant, grant);
  }

  public isFeatureEnabled(feature: Feature): boolean {
    return this.org?.isFeatureEnabled(feature) ?? false;
  }

  private isGranted(permission: PermissionGrant | undefined, grant: PermissionGrant): boolean {
    if (isDefined(permission)) {
      if (permission === grant) {
        return true;
      }
      let result: boolean = grant !== PermissionGrant.NONE;
      if (this.hasDelete(grant)) {
        result = result && this.hasDelete(permission);
      }
      if (this.hasWrite(grant)) {
        result = result && this.hasWrite(permission);
      }
      if (this.hasRead(grant)) {
        result = result && this.hasRead(permission);
      }
      return result;
    }
    return false;
  }

  private hasRead(grant: PermissionGrant): boolean {
    switch (grant) {
      case PermissionGrant.R:
      case PermissionGrant.RW:
      case PermissionGrant.RWD:
        return true;
      default:
        return false;
    }
  }

  private hasWrite(grant: PermissionGrant): boolean {
    switch (grant) {
      case PermissionGrant.RW:
      case PermissionGrant.RWD:
        return true;
      default:
        return false;
    }
  }

  private hasDelete(grant: PermissionGrant): boolean {
    switch (grant) {
      case PermissionGrant.RWD:
        return true;
      default:
        return false;
    }
  }
}
